var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var LagConfig_1;
import { DateFrequency, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './lagAggregateColumn'; // force import
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
import { lagConfigSchemaValidation } from '../validations.generated';
export const lagConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:LagConfig',
    type: 'object',
    properties: {
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        },
        dateColumn: { type: 'string', minLength: 1 },
        frequency: {
            type: 'string',
            enum: Object.keys(DateFrequency).map((key) => DateFrequency[key])
        },
        lagAggregateColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:LagAggregateColumn' },
            uniqueItemProperties: ['alias']
        },
        inclusive: { type: 'boolean' }
    },
    required: ['groupByColumns', 'dateColumn', 'frequency', 'lagAggregateColumns', 'inclusive']
};
let LagConfig = LagConfig_1 = class LagConfig extends ToolConfig {
    constructor(groupByColumns, dateColumn, frequency, lagAggregateColumns, inclusive) {
        super(ToolType.LAG);
        this.groupByColumns = groupByColumns;
        this.dateColumn = dateColumn;
        this.frequency = frequency;
        this.lagAggregateColumns = lagAggregateColumns;
        this.inclusive = inclusive;
        JSON.validate(this, LagConfig_1);
    }
    get in() {
        return this.ins[0];
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return LagConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return LagConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return LagConfig_1.outs;
    }
    static get in() {
        return LagConfig_1.ins[0];
    }
    static get out() {
        return LagConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.LAG.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['interval'], true);
    }
    getBaseValues(parameters) {
        return {
            groupByColumns: this.groupByColumns,
            dateColumn: this.dateColumn,
            frequency: this.frequency,
            lagAggregateColumns: this.lagAggregateColumns.map((a) => a.getBaseValues(parameters)),
            inclusive: this.inclusive
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const baseVals = this.getBaseValues(iConfigHelper.getParameters());
            const dateColSource = exp.findSourceColumn(this.in, baseVals.dateColumn);
            const aggColsSource = [];
            const aggColumns = LagConfig_1.getUniqueAggColumns(baseVals.lagAggregateColumns);
            aggColumns.forEach((colName) => {
                aggColsSource.push(exp.findSourceColumn(this.in, colName));
            });
            const dependents = [dateColSource];
            dependents.push(...this.getGroupByDependents(exp, this.in, baseVals.groupByColumns), ...aggColsSource);
            exp.copy(this.in, dateColSource, false, true);
            aggColsSource.forEach((colSource) => {
                exp.copy(this.in, colSource, false, true);
            });
            const newLagCols = LagConfig_1.getNewLagColumns(baseVals);
            newLagCols.forEach((colName) => {
                exp.makeNew(new Column(colName, 'number'), dependents, true);
            });
        }
        return tsb.build();
    }
    static getNewLagColumns(baseVal) {
        // define the list of the new columns to be added to the tool's output
        // this.inclusive define if we show all the lag steps for each new lag column
        const colsNames = [];
        for (let i = 0; i < baseVal.lagAggregateColumns.length; i++) {
            if (baseVal.inclusive) {
                for (let j = 1; j <= baseVal.lagAggregateColumns[i].lag; j++) {
                    colsNames.push(`${baseVal.lagAggregateColumns[i].alias}_${j}`);
                }
            }
            else {
                colsNames.push(baseVal.lagAggregateColumns[i].alias);
            }
        }
        return colsNames;
    }
    static getUniqueAggColumns(lagCols) {
        // one same column can be used multiple times for creating new lag columns
        // here, we get a unique set of the column's names used to define the new lag columns
        const uniqueAggColumns = new Set();
        lagCols.forEach((aggConfig) => {
            uniqueAggColumns.add(aggConfig.column);
        });
        return uniqueAggColumns;
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        // If inclusive == true:
        // 	limit each group's amount of lag to 100
        //	limit the total amout of lags to 300
        if (baseVals.inclusive) {
            const limit = 100;
            const totalLimit = 300;
            let total = 0;
            for (const col of baseVals.lagAggregateColumns) {
                if (col.lag > limit) {
                    return ToolValidationState.BAD_METADATA;
                }
                total += col.lag;
            }
            if (total > totalLimit) {
                return ToolValidationState.BAD_METADATA;
            }
        }
        const inputs = new Map([[baseVals.dateColumn, ['date']]]);
        const aggColumns = LagConfig_1.getUniqueAggColumns(baseVals.lagAggregateColumns);
        this.addGroupByToInputs(inputs, baseVals.groupByColumns);
        aggColumns.forEach((colName) => {
            inputs.set(colName, ['number']);
        });
        const outputs = [baseVals.dateColumn];
        const newLagCols = LagConfig_1.getNewLagColumns(baseVals);
        outputs.push(...baseVals.groupByColumns, ...aggColumns, ...newLagCols);
        if (!isSourceValid(source, inputs, [])) {
            return ToolValidationState.BAD_METADATA;
        }
        if (new Set(outputs).size !== outputs.length) {
            return ToolValidationState.BAD_METADATA;
        }
        return ToolValidationState.VALID;
    }
};
LagConfig = LagConfig_1 = __decorate([
    JsonSchemaV2(lagConfigSchema, lagConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.LAG)
], LagConfig);
export { LagConfig };
//# sourceMappingURL=lagConfig.js.map